// React Query hooks for the communication-settings sub-tab.
//
// Endpoints live under /api/v1/settings/communication/* and the server
// module is settings/communication. Reads are staff-accessible; writes
// require admin (server enforces).

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getEmailSettings,
  getImportParsers,
  getNotificationPreferences,
  getSmsSettings,
  testEmailConnection,
  testSmsConnection,
  updateEmailSettings,
  updateNotificationPreferences,
  updateSmsSettings,
} from '../../../../api/communication';
import { queryKeys } from '../../../../api/queryKeys';
import { useToast } from '../../../../hooks/useToast';
import type {
  EmailPurpose,
  EmailSettings,
  ImportParsersResponse,
  PreferencesResponse,
  SmsSettings,
  TestEmailInput,
  TestSmsInput,
  UpsertImapEmailInput,
  UpsertPreferencesInput,
  UpsertSmsInput,
  UpsertSmtpEmailInput,
} from '../../../../types/api';

// -- Email settings ---------------------------------------------------------

export function useEmailSettings(purpose: EmailPurpose) {
  return useQuery<EmailSettings>({
    queryKey: queryKeys.communication.email(purpose),
    queryFn: () => getEmailSettings(purpose),
    staleTime: 30_000,
    retry: false,
  });
}

export function useUpdateEmailSettings(purpose: EmailPurpose) {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (input: UpsertSmtpEmailInput | UpsertImapEmailInput) =>
      updateEmailSettings(purpose, input),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: queryKeys.communication.email(purpose),
      });
      void qc.invalidateQueries({
        queryKey: queryKeys.communication.emailList,
      });
      toast.success('Email settings saved.');
    },
    onError: () => {
      toast.error('Failed to save email settings.');
    },
  });
}

export function useTestEmailConnection(purpose: EmailPurpose) {
  return useMutation({
    mutationFn: (input: TestEmailInput) => testEmailConnection(purpose, input),
  });
}

// -- SMS settings -----------------------------------------------------------

export function useSmsSettings() {
  return useQuery<SmsSettings | null>({
    queryKey: queryKeys.communication.sms(),
    queryFn: () => getSmsSettings(),
    staleTime: 30_000,
    retry: false,
  });
}

export function useUpdateSmsSettings() {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (input: UpsertSmsInput) => updateSmsSettings(input),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: queryKeys.communication.sms(),
      });
      toast.success('SMS settings saved.');
    },
    onError: () => {
      toast.error('Failed to save SMS settings.');
    },
  });
}

export function useTestSmsConnection() {
  return useMutation({
    mutationFn: (input: TestSmsInput) => testSmsConnection(input),
  });
}

// -- Notification preferences ----------------------------------------------

export function useNotificationPreferences() {
  return useQuery<PreferencesResponse>({
    queryKey: queryKeys.communication.preferences(),
    queryFn: () => getNotificationPreferences(),
    staleTime: 30_000,
    retry: false,
  });
}

export function useUpdateNotificationPreferences() {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (input: UpsertPreferencesInput) =>
      updateNotificationPreferences(input),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: queryKeys.communication.preferences(),
      });
      toast.success('Preferences saved.');
    },
    onError: () => {
      toast.error('Failed to save preferences.');
    },
  });
}

// -- Import parsers ---------------------------------------------------------

export function useImportParsers() {
  return useQuery<ImportParsersResponse>({
    queryKey: queryKeys.communication.parsers(),
    queryFn: () => getImportParsers(),
    staleTime: 60_000,
    retry: false,
  });
}
